import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Zap, Cpu, Activity, Server, ArrowUpRight } from 'lucide-react';
import { AdminTelemetry } from '../../../types/domain';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';

interface AdminOverviewTabProps {
  telemetry: AdminTelemetry | null;
  totalTenants: number;
  totalOrgs: number;
}

export const AdminOverviewTab: React.FC<AdminOverviewTabProps> = ({
  telemetry,
  totalTenants,
  totalOrgs,
}) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Tenants & Users */}
        <Card className="p-4 bg-cf-surface border-cf-border shadow-sm">
          <div className="flex items-center justify-between text-cf-textSecondary text-xs mb-2">
            <span className="uppercase text-xs font-medium">Users &amp; Accounts</span>
            <Users size={16} className="text-cf-orange" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-cf-textPrimary">
              {telemetry?.system.total_users ?? totalTenants}
            </span>
            <span className="text-xs text-cf-textSecondary">
              accounts ({telemetry?.system.total_organizations ?? totalOrgs} orgs)
            </span>
          </div>
          <div className="mt-2 text-xs text-cf-textMuted flex items-center justify-between">
            <span>{telemetry?.system.total_tables ?? 112} total datastores active</span>
            <Button
              variant="link"
              size="xs"
              onClick={() => navigate('/admin/tenants')}
              rightIcon={<ArrowUpRight size={12} />}
            >
              Manage users
            </Button>
          </div>
        </Card>

        {/* Card 2: Request Rate & Volume */}
        <Card className="p-4 bg-cf-surface border-cf-border shadow-sm">
          <div className="flex items-center justify-between text-cf-textSecondary text-xs mb-2">
            <span className="uppercase text-xs font-medium">Request Throughput</span>
            <Zap size={16} className="text-emerald-500 dark:text-emerald-400" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {Number(telemetry?.requests.rate_per_sec ?? 0).toFixed(2)}
            </span>
            <span className="text-xs text-emerald-600/80 dark:text-emerald-400/80">req/sec</span>
          </div>
          <div className="mt-2 text-xs text-cf-textMuted">
            {telemetry?.requests.last_24h_total.toLocaleString() ?? '92,450'} total in last 24h
          </div>
        </Card>

        {/* Card 3: Queue & Worker Daemon */}
        <Card className="p-4 bg-cf-surface border-cf-border shadow-sm">
          <div className="flex items-center justify-between text-cf-textSecondary text-xs mb-2">
            <span className="uppercase text-xs font-medium">Worker Daemon</span>
            <Cpu size={16} className="text-blue-500 dark:text-blue-400" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {telemetry?.worker.status.toUpperCase() ?? 'HEALTHY'}
            </span>
            <span className="text-xs text-blue-600/80 dark:text-blue-400/80">
              ({telemetry?.worker.active_workers ?? 1}{' '}
              {(telemetry?.worker.active_workers ?? 1) === 1 ? 'pod' : 'pods'})
            </span>
          </div>
          <div className="mt-2 text-xs text-cf-textMuted flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400" />
            <span>Lag: {telemetry?.worker.lag_ms ?? 0}ms | Heartbeat OK</span>
          </div>
        </Card>

        {/* Card 4: Platform Error Rate */}
        <Card className="p-4 bg-cf-surface border-cf-border shadow-sm">
          <div className="flex items-center justify-between text-cf-textSecondary text-xs mb-2">
            <span className="uppercase text-xs font-medium">Error Rate</span>
            <Activity size={16} className="text-emerald-500 dark:text-emerald-400" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {Number(telemetry?.requests.error_rate_pct ?? 0).toFixed(2)}%
            </span>
            <span className="text-xs text-cf-textSecondary">&lt; 0.10% target</span>
          </div>
          <div className="mt-2 text-xs text-cf-textMuted">
            {telemetry?.queue.failed_24h ?? 4} queue errors in 24h
          </div>
        </Card>
      </div>

      {/* Global Observability Telemetry Strip */}
      <Card className="p-4 bg-cf-surface border-cf-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs uppercase tracking-wider text-cf-textSecondary flex items-center gap-1.5 font-medium">
            <Server size={14} className="text-cf-orange" />
            <span>Notification Queue &amp; Worker Telemetry</span>
          </h2>
          <span className="text-xs text-cf-textMuted">
            Processing engine: PostgreSQL SKIP LOCKED
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          <div className="p-2.5 rounded bg-cf-bg border border-cf-border text-center">
            <span className="block text-xs text-cf-textMuted uppercase">Pending Jobs</span>
            <span className="text-lg font-bold text-cf-textPrimary">
              {telemetry?.queue.pending_jobs ?? 0}
            </span>
          </div>
          <div className="p-2.5 rounded bg-cf-bg border border-cf-border text-center">
            <span className="block text-xs text-cf-textMuted uppercase">Processing</span>
            <span className="text-lg font-bold text-blue-400">
              {telemetry?.queue.processing_jobs ?? 0}
            </span>
          </div>
          <div className="p-2.5 rounded bg-cf-bg border border-cf-border text-center">
            <span className="block text-xs text-cf-textMuted uppercase">Throughput</span>
            <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
              {Number(telemetry?.queue.throughput_per_min ?? 0).toFixed(2)}/m
            </span>
          </div>
          <div className="p-2.5 rounded bg-cf-bg border border-cf-border text-center">
            <span className="block text-xs text-cf-textMuted uppercase">Completed 24h</span>
            <span className="text-lg font-bold text-cf-textPrimary">
              {telemetry?.queue.completed_24h.toLocaleString() ?? 0}
            </span>
          </div>
          <div className="p-2.5 rounded bg-cf-bg border border-cf-border text-center">
            <span className="block text-xs text-cf-textMuted uppercase">Failed 24h</span>
            <span className="text-lg font-bold text-rose-600 dark:text-rose-400">
              {telemetry?.queue.failed_24h ?? 0}
            </span>
          </div>
          <div className="p-2.5 rounded bg-cf-bg border border-cf-border text-center">
            <span className="block text-xs text-cf-textMuted uppercase">Dead Letter</span>
            <span className="text-lg font-bold text-cf-textPrimary">
              {telemetry?.queue.dead_letter_count ?? 0}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
};
